import React, { useState, useEffect } from 'react';
import { submitSendingChurch } from '../../api/onboardingService';

interface SendingChurchStepProps {
  data?: any;
  onComplete: (data: any) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface SendingChurchData {
  // No Sending Church Option
  noSendingChurch: boolean;
  noSendingChurchExplanation: string;
  
  // Church Information
  churchName: string;
  denomination: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  website: string;
  phone: string;
  email: string;
  
  // Primary Contact Information
  primaryContactName: string;
  primaryContactTitle: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  
  // Relationship Details
  relationshipDescription: string;
  yearsAttended: number | '';
  membershipStatus: 'member' | 'regular_attender' | 'visitor' | 'former_member' | '';
  involvementLevel: 'high' | 'moderate' | 'low' | 'minimal' | '';
  
  // Support and Endorsement
  financialSupport: boolean;
  financialSupportAmount: number | '';
  financialSupportFrequency: 'monthly' | 'quarterly' | 'annually' | 'one_time' | '';
  prayerSupport: boolean;
  endorsementProvided: boolean;
  endorsementDate: string;
  endorsementDocument: string;
  
  // Additional Information
  churchSize: 'under_50' | '50_100' | '100_200' | '200_500' | '500_1000' | 'over_1000' | '';
  churchAge: number | '';
  churchPlantingExperience: boolean;
  churchPlantingHistory: string;
  additionalNotes: string;
}

const SendingChurchStep: React.FC<SendingChurchStepProps> = ({ data, onComplete, onNext, onPrevious }) => {
  const [formData, setFormData] = useState<SendingChurchData>({
    noSendingChurch: false,
    noSendingChurchExplanation: '',
    churchName: '',
    denomination: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
    },
    website: '',
    phone: '',
    email: '',
    primaryContactName: '',
    primaryContactTitle: '',
    primaryContactPhone: '',
    primaryContactEmail: '',
    relationshipDescription: '',
    yearsAttended: '',
    membershipStatus: '',
    involvementLevel: '',
    financialSupport: false,
    financialSupportAmount: '',
    financialSupportFrequency: '',
    prayerSupport: false,
    endorsementProvided: false,
    endorsementDate: '',
    endorsementDocument: '',
    churchSize: '',
    churchAge: '',
    churchPlantingExperience: false,
    churchPlantingHistory: '',
    additionalNotes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const autoSaveTimer = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (data) {
      setFormData(prevData => ({ ...prevData, ...data }));
    }
  }, [data]);

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isValidUrl = (value: string) => {
    try {
      const url = value.match(/^https?:\/\//i) ? value : `https://${value}`;
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  const isValidPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const isFormValidForSave = (dataToSave: SendingChurchData) => {
    // If no sending church, only require the explanation
    if (dataToSave.noSendingChurch) {
      return !!dataToSave.noSendingChurchExplanation.trim();
    }

    // Required fields for sending church
    if (!dataToSave.churchName.trim()) return false;
    if (!dataToSave.address.street.trim()) return false;
    if (!dataToSave.address.city.trim()) return false;
    if (!dataToSave.address.state.trim()) return false;
    if (!dataToSave.address.zipCode.trim()) return false;
    if (!dataToSave.primaryContactName.trim()) return false;
    if (!dataToSave.primaryContactTitle.trim()) return false;
    if (!dataToSave.relationshipDescription.trim()) return false;
    if (!dataToSave.membershipStatus) return false;
    if (!dataToSave.involvementLevel) return false;

    if (dataToSave.financialSupport && !dataToSave.financialSupportAmount) return false;
    if (dataToSave.financialSupport && !dataToSave.financialSupportFrequency) return false;
    if (dataToSave.endorsementProvided && !dataToSave.endorsementDate) return false;

    // Optional fields with format validation
    if (dataToSave.website && !isValidUrl(dataToSave.website)) return false;
    if (dataToSave.phone && !isValidPhone(dataToSave.phone)) return false;
    if (dataToSave.email && !isValidEmail(dataToSave.email)) return false;
    if (dataToSave.primaryContactPhone && !isValidPhone(dataToSave.primaryContactPhone)) return false;
    if (dataToSave.primaryContactEmail && !isValidEmail(dataToSave.primaryContactEmail)) return false;

    return true;
  };

  // Debounced auto-save
  const autoSaveDraft = React.useCallback((dataToSave: SendingChurchData) => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }

    autoSaveTimer.current = setTimeout(async () => {
      if (!isFormValidForSave(dataToSave)) {
        return;
      }

      try {
        setIsSaving(true);
        // Save draft without triggering onboarding progression
        await submitSendingChurch(dataToSave as any);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      } catch (error) {
        console.error('Error auto-saving sending church data:', error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
  }, [onComplete]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // If no sending church, only validate the explanation
    if (formData.noSendingChurch) {
      if (!formData.noSendingChurchExplanation.trim()) {
        newErrors.noSendingChurchExplanation = 'Please explain your situation';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Otherwise, validate sending church fields
    if (!formData.churchName.trim()) {
      newErrors.churchName = 'Church name is required';
    }

    if (!formData.address.street.trim()) {
      newErrors['address.street'] = 'Street address is required';
    }

    if (!formData.address.city.trim()) {
      newErrors['address.city'] = 'City is required';
    }

    if (!formData.address.state.trim()) {
      newErrors['address.state'] = 'State is required';
    }

    if (!formData.address.zipCode.trim()) {
      newErrors['address.zipCode'] = 'ZIP code is required';
    }

    if (!formData.primaryContactName.trim()) {
      newErrors.primaryContactName = 'Primary contact name is required';
    }

    if (!formData.primaryContactTitle.trim()) {
      newErrors.primaryContactTitle = 'Primary contact title is required';
    }

    if (!formData.relationshipDescription.trim()) {
      newErrors.relationshipDescription = 'Relationship description is required';
    }

    if (!formData.membershipStatus) {
      newErrors.membershipStatus = 'Membership status is required';
    }

    if (!formData.involvementLevel) {
      newErrors.involvementLevel = 'Involvement level is required';
    }

    if (formData.financialSupport && !formData.financialSupportAmount) {
      newErrors.financialSupportAmount = 'Financial support amount is required when financial support is provided';
    }

    if (formData.financialSupport && !formData.financialSupportFrequency) {
      newErrors.financialSupportFrequency = 'Financial support frequency is required when financial support is provided';
    }

    if (formData.endorsementProvided && !formData.endorsementDate) {
      newErrors.endorsementDate = 'Endorsement date is required when endorsement is provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'website' && value && !value.match(/^https?:\/\//i)) {
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: `http://${value}`,
        };
        autoSaveDraft(updated);
        return updated;
      });

      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: '',
        }));
      }

      return;
    }

    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => {
        const updated = {
          ...prev,
          address: {
            ...prev.address,
            [field]: value,
          },
        };
        autoSaveDraft(updated);
        return updated;
      });
    } else if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: checked,
        };
        autoSaveDraft(updated);
        return updated;
      });
    } else if (type === 'number') {
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: value ? parseFloat(value) : '',
        };
        autoSaveDraft(updated);
        return updated;
      });
    } else {
      setFormData(prev => {
        const updated = {
          ...prev,
          [name]: value,
        };
        autoSaveDraft(updated);
        return updated;
      });
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      await onComplete(formData);
    } catch (error) {
      console.error('Error saving sending church data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Sending Church Information</h2>
        <p className="text-gray-600">
          Please provide information about your sending church. This is the church that is supporting and endorsing your church planting ministry.
        </p>
        <div className="mt-4 p-4 bg-green-50 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Note:</strong> Your sending church will be contacted to verify the information provided and confirm their support for your church planting ministry.
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* No Sending Church Option */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="noSendingChurch"
              name="noSendingChurch"
              checked={formData.noSendingChurch}
              onChange={handleInputChange}
              className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
            />
            <label htmlFor="noSendingChurch" className="ml-3">
              <span className="block text-sm font-medium text-gray-900">I do not currently have a sending church</span>
              <span className="block text-sm text-gray-600 mt-1">
                Check this if you are not currently connected with a sending church that is supporting your ministry.
              </span>
            </label>
          </div>

      {formData.noSendingChurch && (
            <div className="mt-4">
              <label htmlFor="noSendingChurchExplanation" className="block text-sm font-medium text-gray-700 mb-2">
                Please explain your situation *
              </label>
              <textarea
                id="noSendingChurchExplanation"
                name="noSendingChurchExplanation"
                value={formData.noSendingChurchExplanation}
                onChange={handleInputChange}
                rows={4}
                className={`block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.noSendingChurchExplanation ? 'border-red-300' : ''
                }`}
                placeholder="Please describe your current church affiliation status, how you plan to establish church support, or any other relevant information..."
              />
              {errors.noSendingChurchExplanation && (
                <p className="mt-1 text-sm text-red-600">{errors.noSendingChurchExplanation}</p>
              )}
            </div>
          )}
        </div>

        {!formData.noSendingChurch && (
          <>
            {/* Church Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Church Information</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="churchName" className="block text-sm font-medium text-gray-700">
                    Church Name *
                  </label>
              <input
                type="text"
                name="churchName"
                id="churchName"
                value={formData.churchName}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.churchName ? 'border-red-300' : ''
                }`}
                placeholder="Church name"
              />
              {errors.churchName && <p className="mt-1 text-sm text-red-600">{errors.churchName}</p>}
            </div>

            <div>
              <label htmlFor="denomination" className="block text-sm font-medium text-gray-700">
                Denomination
              </label>
              <input
                type="text"
                name="denomination"
                id="denomination"
                value={formData.denomination}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Denomination"
              />
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                Website
              </label>
              <input
                type="url"
                name="website"
                id="website"
                value={formData.website}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Church Phone
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Phone number"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Church Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Email address"
              />
            </div>
          </div>
        </div>

        {/* Address Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Church Address</h3>
          <div className="space-y-6">
            <div>
              <label htmlFor="address.street" className="block text-sm font-medium text-gray-700">
                Street Address *
              </label>
              <input
                type="text"
                name="address.street"
                id="address.street"
                value={formData.address.street}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors['address.street'] ? 'border-red-300' : ''
                }`}
                placeholder="Street address"
              />
              {errors['address.street'] && <p className="mt-1 text-sm text-red-600">{errors['address.street']}</p>}
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label htmlFor="address.city" className="block text-sm font-medium text-gray-700">
                  City *
                </label>
                <input
                  type="text"
                  name="address.city"
                  id="address.city"
                  value={formData.address.city}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    errors['address.city'] ? 'border-red-300' : ''
                  }`}
                  placeholder="City"
                />
                {errors['address.city'] && <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>}
              </div>

              <div>
                <label htmlFor="address.state" className="block text-sm font-medium text-gray-700">
                  State *
                </label>
                <input
                  type="text"
                  name="address.state"
                  id="address.state"
                  value={formData.address.state}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    errors['address.state'] ? 'border-red-300' : ''
                  }`}
                  placeholder="State"
                />
                {errors['address.state'] && <p className="mt-1 text-sm text-red-600">{errors['address.state']}</p>}
              </div>

              <div>
                <label htmlFor="address.zipCode" className="block text-sm font-medium text-gray-700">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  name="address.zipCode"
                  id="address.zipCode"
                  value={formData.address.zipCode}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                    errors['address.zipCode'] ? 'border-red-300' : ''
                  }`}
                  placeholder="ZIP code"
                />
                {errors['address.zipCode'] && <p className="mt-1 text-sm text-red-600">{errors['address.zipCode']}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Primary Contact Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Primary Contact Information</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="primaryContactName" className="block text-sm font-medium text-gray-700">
                Contact Name *
              </label>
              <input
                type="text"
                name="primaryContactName"
                id="primaryContactName"
                value={formData.primaryContactName}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.primaryContactName ? 'border-red-300' : ''
                }`}
                placeholder="Contact name"
              />
              {errors.primaryContactName && <p className="mt-1 text-sm text-red-600">{errors.primaryContactName}</p>}
            </div>

            <div>
              <label htmlFor="primaryContactTitle" className="block text-sm font-medium text-gray-700">
                Contact Title *
              </label>
              <input
                type="text"
                name="primaryContactTitle"
                id="primaryContactTitle"
                value={formData.primaryContactTitle}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.primaryContactTitle ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Senior Pastor, Missions Pastor"
              />
              {errors.primaryContactTitle && <p className="mt-1 text-sm text-red-600">{errors.primaryContactTitle}</p>}
            </div>

            <div>
              <label htmlFor="primaryContactPhone" className="block text-sm font-medium text-gray-700">
                Contact Phone
              </label>
              <input
                type="tel"
                name="primaryContactPhone"
                id="primaryContactPhone"
                value={formData.primaryContactPhone}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Phone number"
              />
            </div>

            <div>
              <label htmlFor="primaryContactEmail" className="block text-sm font-medium text-gray-700">
                Contact Email
              </label>
              <input
                type="email"
                name="primaryContactEmail"
                id="primaryContactEmail"
                value={formData.primaryContactEmail}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Email address"
              />
            </div>
          </div>
        </div>

        {/* Relationship Details */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Your Relationship with the Church</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="yearsAttended" className="block text-sm font-medium text-gray-700">
                Years Attended
              </label>
              <input
                type="number"
                name="yearsAttended"
                id="yearsAttended"
                min="0"
                max="50"
                value={formData.yearsAttended}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Years"
              />
            </div>

            <div>
              <label htmlFor="membershipStatus" className="block text-sm font-medium text-gray-700">
                Membership Status *
              </label>
              <select
                name="membershipStatus"
                id="membershipStatus"
                value={formData.membershipStatus}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.membershipStatus ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select status</option>
                <option value="member">Member</option>
                <option value="regular_attender">Regular Attender</option>
                <option value="visitor">Visitor</option>
                <option value="former_member">Former Member</option>
              </select>
              {errors.membershipStatus && <p className="mt-1 text-sm text-red-600">{errors.membershipStatus}</p>}
            </div>

            <div>
              <label htmlFor="involvementLevel" className="block text-sm font-medium text-gray-700">
                Involvement Level *
              </label>
              <select
                name="involvementLevel"
                id="involvementLevel"
                value={formData.involvementLevel}
                onChange={handleInputChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                  errors.involvementLevel ? 'border-red-300' : ''
                }`}
              >
                <option value="">Select level</option>
                <option value="high">High</option>
                <option value="moderate">Moderate</option>
                <option value="low">Low</option>
                <option value="minimal">Minimal</option>
              </select>
              {errors.involvementLevel && <p className="mt-1 text-sm text-red-600">{errors.involvementLevel}</p>}
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="relationshipDescription" className="block text-sm font-medium text-gray-700">
              Relationship Description *
            </label>
            <textarea
              name="relationshipDescription"
              id="relationshipDescription"
              rows={4}
              value={formData.relationshipDescription}
              onChange={handleInputChange}
              className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                errors.relationshipDescription ? 'border-red-300' : ''
              }`}
              placeholder="Describe your relationship with this church and how they are supporting your church planting ministry..."
            />
            {errors.relationshipDescription && <p className="mt-1 text-sm text-red-600">{errors.relationshipDescription}</p>}
          </div>
        </div>

        {/* Support and Endorsement */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Support and Endorsement</h3>
          <div className="space-y-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="financialSupport"
                id="financialSupport"
                checked={formData.financialSupport}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="financialSupport" className="ml-2 block text-sm text-gray-900">
                Financial Support
              </label>
            </div>

            {formData.financialSupport && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 pl-6">
                <div>
                  <label htmlFor="financialSupportAmount" className="block text-sm font-medium text-gray-700">
                    Support Amount *
                  </label>
                  <input
                    type="number"
                    name="financialSupportAmount"
                    id="financialSupportAmount"
                    min="0"
                    step="0.01"
                    value={formData.financialSupportAmount}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.financialSupportAmount ? 'border-red-300' : ''
                    }`}
                    placeholder="Amount"
                  />
                  {errors.financialSupportAmount && <p className="mt-1 text-sm text-red-600">{errors.financialSupportAmount}</p>}
                </div>

                <div>
                  <label htmlFor="financialSupportFrequency" className="block text-sm font-medium text-gray-700">
                    Support Frequency *
                  </label>
                  <select
                    name="financialSupportFrequency"
                    id="financialSupportFrequency"
                    value={formData.financialSupportFrequency}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.financialSupportFrequency ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="">Select frequency</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                    <option value="one_time">One-time</option>
                  </select>
                  {errors.financialSupportFrequency && <p className="mt-1 text-sm text-red-600">{errors.financialSupportFrequency}</p>}
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                name="prayerSupport"
                id="prayerSupport"
                checked={formData.prayerSupport}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="prayerSupport" className="ml-2 block text-sm text-gray-900">
                Prayer Support
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="endorsementProvided"
                id="endorsementProvided"
                checked={formData.endorsementProvided}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="endorsementProvided" className="ml-2 block text-sm text-gray-900">
                Endorsement Provided
              </label>
            </div>

            {formData.endorsementProvided && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 pl-6">
                <div>
                  <label htmlFor="endorsementDate" className="block text-sm font-medium text-gray-700">
                    Endorsement Date *
                  </label>
                  <input
                    type="date"
                    name="endorsementDate"
                    id="endorsementDate"
                    value={formData.endorsementDate}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                      errors.endorsementDate ? 'border-red-300' : ''
                    }`}
                  />
                  {errors.endorsementDate && <p className="mt-1 text-sm text-red-600">{errors.endorsementDate}</p>}
                </div>

                <div>
                  <label htmlFor="endorsementDocument" className="block text-sm font-medium text-gray-700">
                    Endorsement Document
                  </label>
                  <input
                    type="text"
                    name="endorsementDocument"
                    id="endorsementDocument"
                    value={formData.endorsementDocument}
                    onChange={handleInputChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Document name or reference"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Information */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="churchSize" className="block text-sm font-medium text-gray-700">
                Church Size
              </label>
              <select
                name="churchSize"
                id="churchSize"
                value={formData.churchSize}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">Select size</option>
                <option value="under_50">Under 50</option>
                <option value="50_100">50-100</option>
                <option value="100_200">100-200</option>
                <option value="200_500">200-500</option>
                <option value="500_1000">500-1000</option>
                <option value="over_1000">Over 1000</option>
              </select>
            </div>

            <div>
              <label htmlFor="churchAge" className="block text-sm font-medium text-gray-700">
                Church Age (years)
              </label>
              <input
                type="number"
                name="churchAge"
                id="churchAge"
                min="0"
                value={formData.churchAge}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Years"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="churchPlantingExperience"
                id="churchPlantingExperience"
                checked={formData.churchPlantingExperience}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="churchPlantingExperience" className="ml-2 block text-sm text-gray-900">
                Church has previous church planting experience
              </label>
            </div>
          </div>

          {formData.churchPlantingExperience && (
            <div className="mt-6">
              <label htmlFor="churchPlantingHistory" className="block text-sm font-medium text-gray-700">
                Church Planting History
              </label>
              <textarea
                name="churchPlantingHistory"
                id="churchPlantingHistory"
                rows={4}
                value={formData.churchPlantingHistory}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Describe the church's previous church planting experience..."
              />
            </div>
          )}

          <div className="mt-6">
            <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700">
              Additional Notes
            </label>
            <textarea
              name="additionalNotes"
              id="additionalNotes"
              rows={3}
              value={formData.additionalNotes}
              onChange={handleInputChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Any additional information about your sending church..."
            />
          </div>
        </div>
          </>
        )}

      {/* Save Success Message */}
      {saveSuccess && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full border border-green-200 bg-white px-3 py-2 shadow-sm">
          <svg className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-medium text-green-700">Saved</span>
        </div>
      )}

      {/* Form Actions */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous Step
          </button>

          <button
            type="submit"
            disabled={isLoading || isSaving}
            className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading || isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save and Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SendingChurchStep;
